import CRC32 from 'crc-32'
import { getAnalytics, logEvent } from 'firebase/analytics'
import { getStorage, ref, uploadBytes } from 'firebase/storage'
import { add, div, gt, lt, mul, sub } from '../../utils/math'

import { getApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import {
    CRC_SKIP_BYTES,
    DATA_LEN_V5,
    DATA_LEN_V6,
    DELAY_MS_BEFORE_FETCH_FLIGHT,
    DeviceBuffer,
    DeviceView,
    MAX_FORMAT_VERSION,
    MAX_HARDWARE_VERSION,
    NUM_POINTS,
    TRANSFER_FORMAT_VERSION,
    VERSION_8K_DPS,
    VERSION_CRC,
    VERSION_MAG,
    VERSION_START_Q,
} from '~/consts/device'
import { setDeviceBattery, setDeviceBatteryStatus, setDeviceStatus, setLastThrowUpload } from '~/signals/devices'
import type { ThrowUploadMetadata } from '~/types/throws'
import { BluetoothStatus } from './useBluetooth'
import { getDeviceId, getDeviceUid } from './utils'

let index = 0
let transferFormatVersion = TRANSFER_FORMAT_VERSION
let transferFlightType = 0
let lastNotificationMs = 0
let lastTransferMs = 0
let lastUpload: number | null = null
let flightDownloading: NodeJS.Timeout | null = null
let lastUploadCrc: number | null = null

export const handleTxCharacteristic =
    async (
        device: BluetoothDevice,
        characteristics: Map<string, BluetoothRemoteGATTCharacteristic>,
        sendMessage: (message: string) => Promise<void>,
    ) =>
    async (event: Event) => {
        const firebaseApp = getApp()
        const auth = getAuth(firebaseApp)
        const storage = getStorage(firebaseApp)
        const analytics = getAnalytics(firebaseApp)

        let nacking = false
        const now = Date.now()
        const value = (event.target as BluetoothRemoteGATTCharacteristic)?.value

        if (value) {
            console.debug(`Notification size: ${value.byteLength} bytes`)

            const timeoutMs = transferFormatVersion <= VERSION_START_Q ? 5000 : 2500
            const deviceTimeout = index !== 0 && gt(sub(now, lastNotificationMs), timeoutMs)

            if (deviceTimeout) {
                console.debug('Starting a new transfer due to timeout', index)
                index = 0
            }

            lastNotificationMs = now
            if (index === 0) {
                lastTransferMs = now
            }

            const DATA_LEN = transferFormatVersion >= VERSION_MAG ? DATA_LEN_V6 : DATA_LEN_V5

            let bytesToTransfer = DATA_LEN
            bytesToTransfer += 4

            if (value.byteLength === 4 && index !== DATA_LEN && index + 4 !== DATA_LEN) {
                // this is an out of place CRC, next notification will be a new transfer
                console.debug('> Out of place CRC: ' + index)
                index = 0
                nacking = false
                return
            }

            for (let i = 0; i < value.byteLength; i++) {
                if (index >= bytesToTransfer) {
                    console.debug('> Extra Data. bytes: ' + value.byteLength)

                    // sending a nack will have the device begin a new transfer
                    // if (transferFormatVersion >= VERSION_8K_DPS) {
                    if (!nacking) {
                        sendMessage('nack')
                        nacking = true
                    }
                    // }
                    return
                }
                DeviceView.setUint8(index++, value.getUint8(i))
            }

            if (index === value.byteLength) {
                // is the first batch of data
                const formatVersion = DeviceView.getUint8(0)
                const hardwareVersion = DeviceView.getUint8(1)
                const startIndex = DeviceView.getUint16(2, true)
                // const timeSinceThrow = DeviceView.getUint16(4, true) * 10
                const flightType = DeviceView.getUint8(6)
                const numPoints = DeviceView.getUint8(7) * 100
                if (
                    formatVersion > MAX_FORMAT_VERSION ||
                    hardwareVersion > MAX_HARDWARE_VERSION ||
                    startIndex >= NUM_POINTS ||
                    (formatVersion >= VERSION_8K_DPS && numPoints != NUM_POINTS)
                ) {
                    // if batch starts with an invalid header, we just discard it
                    console.debug('> INVALID THROW DATA HEADER')
                    if (!nacking) {
                        await sendMessage('nack')
                        nacking = true
                    }
                    index = bytesToTransfer
                    return
                }

                transferFormatVersion = formatVersion
                transferFlightType = flightType
            }

            if (index === bytesToTransfer) {
                console.debug('> Transfer Complete')
                const buf = DeviceBuffer.slice(0, DATA_LEN)
                const crc: number = CRC32.buf(new Uint8Array(buf, CRC_SKIP_BYTES))
                if (transferFormatVersion >= VERSION_CRC) {
                    const passedCrc = DeviceView.getInt32(DATA_LEN, true)
                    if (crc !== passedCrc) {
                        // crc is wrong
                        console.debug('> CRC is WRONG')
                        index = 0
                        return
                    }
                }

                if (lastUploadCrc && crc == lastUploadCrc) {
                    // data sent twice
                    console.debug('> Data sent twice. acking again.')
                    await sendMessage('ack')
                    return
                }

                const bleTransferMillis = Date.now() - lastTransferMs

                console.debug('> Transfer complete. Storing data...')
                setDeviceStatus(BluetoothStatus.Ready)

                lastUploadCrc = crc
                let seconds = Math.floor(lastTransferMs / 1000)
                let suffix = 'throw'
                const isThrow = transferFlightType === 0

                if (!isThrow) {
                    if (flightDownloading && lastUpload) {
                        seconds = lastUpload
                        suffix = 'flight'
                        clearTimeout(flightDownloading)
                        flightDownloading = null
                    } else {
                        console.error("Recieved a flight when we weren't expecting one.")
                        return
                    }
                }

                const throwRef = ref(
                    storage,
                    `/raw-throws/${auth.currentUser?.uid}/${seconds}.${suffix}`,
                )

                const uidChar = characteristics.get('uid')
                const macChar = characteristics.get('mac')

                const deviceId = macChar ? await getDeviceId(device, macChar) : undefined
                const deviceUid = uidChar ? await getDeviceUid(uidChar) : undefined
                const uploadthrowMillis = Date.now() - lastTransferMs - bleTransferMillis

                // TODO: suspect
                if (!deviceId) {
                    console.error('No device id')
                    return
                }
                // TODO: suspect
                if (!deviceUid) {
                    console.error('No device uid')
                    return
                }
                const customMetadata: ThrowUploadMetadata = {
                    deviceId: deviceId,
                    userId: auth.currentUser?.uid ?? '',
                    deviceBeginTransfer: lastTransferMs.toString(),
                    bluetoothMillis: bleTransferMillis.toString(),
                    uploadMillis: uploadthrowMillis.toString(),
                    client: 'web',
                }
                console.log(customMetadata)
                if (deviceUid) {
                    customMetadata.deviceUid = deviceUid
                }

                // if (deviceSoftware) {
                //   customMetadata.deviceSoftware = deviceSoftware
                // }
                try {
                    await uploadBytes(throwRef, buf, { customMetadata: customMetadata as unknown as { [key: string]: string } })

                    //   setTransferring(false)
                    // The bytes were successfully uploaded,
                    // We ack the transfer to the device and trigger a summarize
                    // Also store the stub object with tags and uploadTime

                    index = 0
                    lastUpload = seconds
                    setLastThrowUpload(seconds)
                    if (!isThrow) {
                        // Clear out last upload after we have fetched the flight.
                        lastUpload = 0
                        logEvent(analytics, 'upload_flight', { ...customMetadata })
                    } else {
                        logEvent(analytics, 'upload_throw', { ...customMetadata })
                        logEvent(analytics, 'throw_upload_latency', {
                            ...customMetadata,
                        })
                    }
                    if (isThrow) {
                        await sendMessage('ack')
                        await new Promise(r => setTimeout(r, DELAY_MS_BEFORE_FETCH_FLIGHT))
                        // downloadFlight(flightDownloading, sendMessage);
                    }
                } catch (error) {
                    //   setTransferring(false)
                    console.error('Upload failed: ' + error)
                    logEvent(analytics, 'upload_failed', {
                        error,
                        path: throwRef.fullPath,
                        ...customMetadata,
                    })
                    console.debug({
                        message: 'Throw failed to store. Make sure internet is working.',
                        intent: 'danger',
                    })
                    index = 0
                }
            }
        }
    }

export const handleBatteryLevel = (batteryLevel: DataView) => {
    const batteryPercent = batteryLevel.getUint8(0)
    let avg = batteryPercent
    if (lt(avg, 0)) {
        setDeviceBattery(0)
    }
    const alpha = div(1.0, 10.0).toNumber()
    const alpha2 = div(1.0, 15.0).toNumber()
    avg = mul(add(mul(Math.floor(avg), alpha), avg), 1 - alpha).toNumber()
    avg = mul(add(mul(batteryPercent, alpha2), avg), 1 - alpha2).toNumber()
    setDeviceBattery(avg)
    setDeviceBatteryStatus(
        avg < 10 ? 'needs-charging' : avg < 30 ? 'low' : avg < 75 ? 'ok' : 'full',
    )
}

export const handleCalibrationValues =
    (characteristics: Map<string, BluetoothRemoteGATTCharacteristic>) => () => {
        const debugNumbers = characteristics.get('debug.numbers')
        const debugVectors = characteristics.get('debug.vectors')
    }
